import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useSpring, MotionValue } from 'framer-motion';
import { Canvas } from '@react-three/fiber';
import { OrbitalGallery } from './OrbitalGallery';
import api, { assetUrl } from '../services/api';

type EnhancedHeroCard = {
  id: string;
  heading: string;
  description: string;
  image: string;
};

type HeroIntroContent = {
  heading: string;
  description: string;
  button_text: string;
  button_action: 'modal' | 'link';
  button_link: string;
  layout_style: 'style1' | 'style2';
  hero_background_type: 'none' | 'image' | 'video';
  hero_background_media: string;
  hero_background_fit: 'fit' | 'cover';
};

const AmbientLight = 'ambientLight' as any;

const HeroCardItem: React.FC<{ 
  card: EnhancedHeroCard; 
  index: number; 
  scrollYProgress: MotionValue<number>;
  isMobile: boolean;
  totalCards: number;
}> = ({ card, index, scrollYProgress, isMobile, totalCards }) => {
  
  // DESKTOP - Original animation
  if (!isMobile) {
    const startTransition = 0.01;
    const endTransition = 0.08;
    
    const stackOffsets = [
      { x: -460, y: 100, rotate: -15 },
      { x: -420, y: 130, rotate: 10 },
      { x: -500, y: 70, rotate: -5 },
      { x: -450, y: 115, rotate: 15 },
    ];

    const initial = stackOffsets[index % 4];
    const spacing = 330;
    const rowX = (index - 1.5) * spacing; 
    const rowY = 150; 

    const initialScale = 1.0;
    const targetScale = 0.85;

    const xTranslate = useTransform(scrollYProgress, 
      [0, startTransition, endTransition], 
      [initial.x, initial.x, rowX]
    );
    
    const yTranslate = useTransform(scrollYProgress, 
      [0, startTransition, endTransition], 
      [initial.y, initial.y, rowY] 
    ); 

    const rotateZ = useTransform(scrollYProgress, [startTransition, endTransition], [initial.rotate, 0]);
    const scale = useTransform(scrollYProgress, 
      [startTransition, endTransition], 
      [initialScale, targetScale]
    );
    
    const opacity = useTransform(scrollYProgress, 
      [0, 0.98, 1.0], 
      [1, 1, 1] 
    );

    const springConfig = { stiffness: 90, damping: 26 };
    const springX = useSpring(xTranslate, springConfig);
    const springY = useSpring(yTranslate, springConfig);
    const springRotate = useSpring(rotateZ, springConfig);
    const springScale = useSpring(scale, springConfig);

    return (
      <motion.div
        style={{ 
          x: springX,
          y: springY, 
          rotateZ: springRotate,
          scale: springScale,
          opacity,
          zIndex: 50 - index,
          left: "50%",
          top: "50%",
          translateX: "-50%",
          translateY: "-50%",
          position: 'absolute',
        }}
        className="w-[320px] aspect-[3/4.5] group"
      >
        <motion.div 
          className="absolute inset-0 bg-[#0a0a0a] rounded-[2.5rem] overflow-hidden border border-white/10 shadow-[0_40px_100px_rgba(0,0,0,1)] transition-all duration-700 group-hover:border-[#FFBF00] group-hover:scale-105 group-hover:z-50"
        >
          <div className="absolute inset-0 w-full h-full">
            <img 
              src={card.image} 
              alt={card.heading} 
              className="w-full h-full object-cover grayscale-[40%] group-hover:grayscale-0 group-hover:scale-110 group-hover:brightness-110 transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
          </div>

          <div className="absolute inset-0 p-8 flex flex-col justify-end z-10 group-hover:bg-gradient-to-t group-hover:from-black/80 group-hover:via-black/40 group-hover:to-transparent transition-all duration-700">
            
            <h4 className="text-4xl font-black uppercase tracking-tighter text-white leading-[0.85] mb-4 group-hover:text-[#FFBF00] transition-colors">
              {card.heading}
            </h4>
             
            <div className="overflow-hidden">
              <p className="text-gray-300 text-base font-medium leading-relaxed">
                {card.description}
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  }

  // MOBILE - 3 SCROLLS COMPLETE
  const totalPairs = Math.ceil(totalCards / 2);
  const pairIndex = Math.floor(index / 2);
  const positionInPair = index % 2;
  
  // 3 SCROLL PHASES
  const stackedPhase = 0.15;
  const firstPairStart = 0.35;
  const secondPairStart = 0.65;
  const sectionEnd = 0.9;
  
  // Stacked positions
  const stackedOffsets = [
    { x: -12, y: -2, rotate: -2 },
    { x: 8, y: 2, rotate: 1.5 },
    { x: -8, y: -3, rotate: -1.5 },
    { x: 10, y: 1, rotate: 1 },
  ];
  
  const stackedOffset = stackedOffsets[index % 4];
  
  // Side by side positions
  const sideBySideOffsets = [
    { x: -100, y: 0, rotate: -1.5 },   // Left card
    { x: 100, y: 3, rotate: 1.5 },      // Right card
  ];
  
  const sideBySideOffset = sideBySideOffsets[positionInPair];
  
  // Opacity
  const opacity = useTransform(scrollYProgress, (progress) => {
    if (progress < stackedPhase) return 1;
    
    if (pairIndex === 0 && progress >= firstPairStart && progress < secondPairStart) return 1;
    
    if (pairIndex === 1 && progress >= secondPairStart && progress < sectionEnd) return 1;
    
    if (progress >= stackedPhase && progress < firstPairStart) {
      const t = (progress - stackedPhase) / (firstPairStart - stackedPhase);
      return pairIndex === 0 ? t : 1 - t;
    }
    
    if (progress >= firstPairStart && progress < secondPairStart) {
      const t = (progress - firstPairStart) / (secondPairStart - firstPairStart);
      return pairIndex === 1 ? t : 1 - t;
    }
    
    return 0;
  });
  
  // X position
  const xTranslate = useTransform(scrollYProgress, (progress) => {
    if (progress < stackedPhase) return stackedOffset.x;
    
    if (pairIndex === 0 && progress >= firstPairStart && progress < secondPairStart) {
      return sideBySideOffset.x;
    }
    
    if (pairIndex === 1 && progress >= secondPairStart && progress < sectionEnd) {
      return sideBySideOffset.x;
    }
    
    if (progress >= stackedPhase && progress < firstPairStart) {
      const t = (progress - stackedPhase) / (firstPairStart - stackedPhase);
      return stackedOffset.x + (sideBySideOffset.x - stackedOffset.x) * t;
    }
    
    return stackedOffset.x;
  });
  
  // Y position
  const yTranslate = useTransform(scrollYProgress, (progress) => {
    if (progress < stackedPhase) return stackedOffset.y;
    
    if (pairIndex === 0 && progress >= firstPairStart && progress < secondPairStart) {
      return positionInPair === 0 ? 0 : 5;
    }
    
    if (pairIndex === 1 && progress >= secondPairStart && progress < sectionEnd) {
      return positionInPair === 0 ? 1 : 6;
    }
    
    return stackedOffset.y;
  });
  
  // Rotation
  const rotateZ = useTransform(scrollYProgress, (progress) => {
    if (progress < stackedPhase) return stackedOffset.rotate;
    
    if (pairIndex === 0 && progress >= firstPairStart && progress < secondPairStart) {
      return positionInPair === 0 ? -1.5 : 1.5;
    }
    
    if (pairIndex === 1 && progress >= secondPairStart && progress < sectionEnd) {
      return positionInPair === 0 ? -1 : 1;
    }
    
    return stackedOffset.rotate;
  });
  
  // Z-index
  const zIndex = useTransform(scrollYProgress, (progress) => {
    if (progress < stackedPhase) {
      return 50 - index;
    }
    
    if (pairIndex === 0 && progress >= firstPairStart && progress < secondPairStart) {
      return positionInPair === 0 ? 60 : 59;
    }
    
    if (pairIndex === 1 && progress >= secondPairStart && progress < sectionEnd) {
      return positionInPair === 0 ? 60 : 59;
    }
    
    return 40;
  });

  // Smooth spring
  const springConfig = { stiffness: 500, damping: 40 };
  const springX = useSpring(xTranslate, springConfig);
  const springY = useSpring(yTranslate, springConfig);
  const springRotate = useSpring(rotateZ, springConfig);
  const springOpacity = useSpring(opacity, springConfig);
  const springZIndex = useSpring(zIndex, springConfig);

  return (
    <motion.div
      style={{ 
        x: springX,
        y: springY,
        rotateZ: springRotate,
        opacity: springOpacity,
        zIndex: springZIndex,
        left: "50%",
        top: "55%",
        translateX: "-50%",
        translateY: "-50%",
        position: 'absolute',
      }}
      className="w-[190px] aspect-[3/4] group"
    >
      <motion.div 
        className="absolute inset-0 bg-[#0a0a0a] rounded-[1.2rem] overflow-hidden border border-white/20 shadow-[0_30px_80px_rgba(0,0,0,1)] transition-all duration-700 group-hover:border-[#FFBF00] group-hover:scale-105 group-hover:z-50"
      >
        <div className="absolute inset-0 w-full h-full">
          <img 
            src={card.image} 
            alt={card.heading} 
            className="w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 group-hover:scale-110 group-hover:brightness-110 transition-all duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        </div>

        <div className="absolute inset-0 p-3 flex flex-col justify-end z-10 group-hover:bg-gradient-to-t group-hover:from-black/90 group-hover:via-black/50 group-hover:to-transparent transition-all duration-700">
          
          <h4 className="text-sm font-black uppercase tracking-tighter text-white leading-[1.2] mb-1 group-hover:text-[#FFBF00] transition-colors">
            {card.heading}
          </h4>
          
          <div className="overflow-hidden">
            <p className="text-gray-300 text-[10px] font-medium leading-relaxed line-clamp-2">
              {card.description}
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const SectionHero: React.FC<{ onContactClick?: () => void }> = ({ onContactClick }) => {
  const containerRef = useRef<HTMLElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [heroCards, setHeroCards] = useState<EnhancedHeroCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [heroIntro, setHeroIntro] = useState<HeroIntroContent>({
    heading: 'SOltechy Innovation',
    description: 'Soltechy transforms ideas into digital solutions.',
    button_text: 'Launch Experience',
    button_action: 'modal',
    button_link: '',
    layout_style: 'style1',
    hero_background_type: 'none',
    hero_background_media: '',
    hero_background_fit: 'fit'
  });

  // Load hero cards from database
  const loadHeroCards = async () => {
    try {
      console.log('🚀 Starting to load hero cards...');
      setLoading(true);
      
      const response = await api.heroApi.getAll();
      console.log('📡 API Response received:', response);
      
      const cards = response.data || [];
      console.log('📦 Raw cards from API:', cards);
      
      const normalizedCards = cards
        .sort((a: any, b: any) => (a.display_order || a.order || 0) - (b.display_order || b.order || 0))
        .map((card: any) => ({
          id: String(card.id),
          heading: String(card.heading || card.title || ''),
          description: String(card.description || card.subtitle || ''),
          image: assetUrl(card.background_image || card.image || card.image_url),
          isActive:
            card.is_active === true ||
            card.is_active === 1 ||
            card.is_active === '1' ||
            card.is_active === 'true'
        }));

      const activeCards = normalizedCards.filter((card: any) => card.isActive);
      const cardsToRender = activeCards.length > 0 ? activeCards : normalizedCards;
      
      setHeroCards(cardsToRender);
      console.log('Loaded hero cards from database:', cardsToRender);
    } catch (error) {
      console.error('❌ Error loading hero cards:', error);
      setHeroCards([]);
    } finally {
      setLoading(false);
    }
  };

  const loadHeroIntro = async () => {
    try {
      const response = await api.sectionApi.get('hero');
      const data = response?.data || {};
      setHeroIntro({
        heading: String(data.heading || 'Soltechy Innovation'),
        description: String(data.description || 'Soltechy transforms ideas into digital solutions.'),
        button_text: String(data.button_text || 'Launch Experience'),
        button_action: data.button_action === 'link' ? 'link' : 'modal',
        button_link: String(data.button_link || ''),
        layout_style: data.layout_style === 'style2' ? 'style2' : 'style1',
        hero_background_type: data.hero_background_type === 'video' ? 'video' : data.hero_background_type === 'image' ? 'image' : 'none',
        hero_background_media: String(data.hero_background_media || ''),
        hero_background_fit: data.hero_background_fit === 'cover' ? 'cover' : 'fit'
      });
    } catch {
      setHeroIntro({
        heading: 'Soltechy Innovation',
        description: 'Soltechy transforms ideas into digital solutions.',
        button_text: 'Launch Experience',
        button_action: 'modal',
        button_link: '',
        layout_style: 'style1',
        hero_background_type: 'none',
        hero_background_media: '',
        hero_background_fit: 'fit'
      });
    }
  };

  useEffect(() => {
    loadHeroCards();
    loadHeroIntro();
    
    const handleDatabaseUpdate = () => {
      console.log('Database update detected, refreshing hero cards...');
      loadHeroCards();
      loadHeroIntro();
    };
    
    window.addEventListener('hero:updated', handleDatabaseUpdate);
    
    return () => {
      window.removeEventListener('hero:updated', handleDatabaseUpdate);
    };
  }, []);

  const introLines = heroIntro.heading.split('\n').map((line) => line.trim()).filter(Boolean);
  const headingFirstLine = introLines[0] || 'Soltechy';
  const headingSecondLine = introLines[1] || '';
  
  const handleHeroButtonClick = () => {
    if (heroIntro.button_action === 'link' && heroIntro.button_link) {
      window.open(heroIntro.button_link, '_blank', 'noopener,noreferrer');
      return;
    }
    onContactClick?.();
  };

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const isStyle2 = heroIntro.layout_style === 'style2';

  // Desktop transforms
  const brandOpacity = useTransform(scrollYProgress, [0, 0.05], [1, 0]);
  const brandY = useTransform(scrollYProgress, [0, 0.05], [0, -150]);

  const strategyOpacity = useTransform(scrollYProgress, [0.04, 0.12, 0.99, 1.0], [0, 1, 1, 1]);
  const strategyY = useTransform(scrollYProgress, [0.04, 0.12], [-30, 0]);

  const frameOpacity = useTransform(scrollYProgress, [0.85, 1.0], [1, 0]);

  // Mobile transforms
  const mobileBrandOpacity = useTransform(scrollYProgress, [0, 0.1, 0.2], [1, 0.5, 0]);
  const mobileBrandY = useTransform(scrollYProgress, [0, 0.2], [0, -30]);
  
  const mobileStrategyOpacity = useTransform(scrollYProgress, [0.15, 0.25, 0.3], [0, 1, 1]);
  const interactionCueOpacity = useTransform(scrollYProgress, [0, isMobile ? 0.15 : 0.05], [1, 0]);
  const style2MediaUrl = heroIntro.hero_background_media ? assetUrl(heroIntro.hero_background_media) : '';
  const style2MediaFitClass = heroIntro.hero_background_fit === 'cover' ? 'object-cover' : 'object-contain';
  const headingStyle2Text = heroIntro.heading
    .replace(/\s*\n+\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (isStyle2) {
    return (
      <section ref={containerRef} className="relative min-h-screen w-full overflow-hidden bg-[#050505] z-30">
        {heroIntro.hero_background_type === 'video' && style2MediaUrl && (
          <video
            src={style2MediaUrl}
            autoPlay
            muted
            loop
            playsInline
            className={`absolute inset-0 h-full w-full ${style2MediaFitClass} object-center`}
          />
        )}
        {heroIntro.hero_background_type === 'image' && style2MediaUrl && (
          <img
            src={style2MediaUrl}
            alt="Hero background"
            className={`absolute inset-0 h-full w-full ${style2MediaFitClass} object-center`}
          />
        )}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,107,53,0.22),transparent_35%),radial-gradient(circle_at_bottom,rgba(255,107,53,0.1),transparent_30%)]" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 min-h-screen w-full flex items-center justify-center px-6 py-20">
          <div className="max-w-5xl w-full text-center">
            <div className="mt-8 space-y-6">
              <motion.div
                initial={{ opacity: 0, x: -80 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, ease: 'easeOut' }}
                className="mx-auto w-fit"
              >
                <div className="relative overflow-hidden rounded-full border border-white/15 bg-white/5 px-5 py-2">
                  <div className="flex items-center gap-3">
                    <span className="h-[1px] w-10 bg-gradient-to-r from-transparent via-[#FFBF00] to-[#FFBF00]" />
                    <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.45em] text-white/70">
                      Visual Experience
                    </span>
                  </div>
                  <motion.span
                    animate={{ x: ['-120%', '140%'] }}
                    transition={{ duration: 1.8, repeat: Infinity, repeatDelay: 0.4, ease: 'linear' }}
                    className="pointer-events-none absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-transparent via-[#FFBF00]/35 to-transparent"
                  />
                </div>
              </motion.div>
              <div className="relative overflow-visible">
                <motion.div
                  initial={{ opacity: 0, scale: 0.94 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.9, ease: 'easeOut' }}
                  className="absolute inset-x-0 top-8 mx-auto h-24 sm:h-28 md:h-32 w-[78%] rounded-full bg-white/8 blur-3xl"
                />
                <div className="relative flex items-center justify-center leading-none uppercase text-white">
                  <motion.h1
                    initial={{ opacity: 0, y: 90, letterSpacing: '0.18em', filter: 'blur(16px)' }}
                    animate={{ opacity: 1, y: 0, letterSpacing: '0.02em', filter: 'blur(0px)' }}
                    transition={{ duration: 0.95, ease: [0.22, 1, 0.36, 1] }}
                    className="mx-auto w-full max-w-[18ch] px-2 text-center font-light text-[clamp(2.9rem,10vw,8.5rem)] leading-[0.9] tracking-[0.02em] whitespace-normal break-words sm:px-4"
                  >
                    {headingStyle2Text || 'Soltechy'}
                  </motion.h1>
                </div>
                <motion.div
                  animate={{ x: ['-25%', '120%'], opacity: [0, 0.55, 0] }}
                  transition={{ duration: 2.4, repeat: Infinity, repeatDelay: 0.2, ease: 'linear' }}
                  className="pointer-events-none absolute inset-y-0 left-0 w-28 sm:w-36 md:w-44 bg-gradient-to-r from-transparent via-white/25 to-transparent blur-xl"
                />
              </div>
              <motion.p
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.15, ease: 'easeOut' }}
                className="max-w-3xl mx-auto text-base sm:text-lg lg:text-xl leading-relaxed text-white/70 text-center whitespace-normal break-words"
              >
                {heroIntro.description}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.65, delay: 0.28, ease: 'easeOut' }}
                className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-3"
              >
                <motion.button
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={handleHeroButtonClick}
                  className="w-full sm:w-auto min-w-[220px] px-8 py-4 bg-[#FFBF00] text-white font-black uppercase tracking-[0.3em] text-xs rounded-full shadow-[0_20px_60px_rgba(255,107,0,0.35)] hover:shadow-[0_20px_80px_rgba(255,107,0,0.5)] transition-all"
                >
                  {heroIntro.button_text || 'Launch Experience'}
                </motion.button>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={containerRef} className="relative h-[300vh] w-full bg-black z-30">
      <motion.div 
        style={{ opacity: frameOpacity }}
        className="sticky top-0 h-screen w-full flex flex-col items-center justify-start overflow-hidden bg-black z-10"
      >
        {/* Animated 3D Background */}
        <div className="absolute inset-0 z-0 opacity-20 pointer-events-none">
          <Canvas camera={{ position: [0, 0, 10], fov: 45 }}>
            <AmbientLight intensity={0.5} />
            <OrbitalGallery />
          </Canvas>
        </div>

        {/* Dynamic UI Content */}
        <div className="relative z-10 w-full max-w-[1700px] h-full flex flex-col items-center md:items-end px-4 md:px-16 overflow-visible">
          
          {/* Desktop Strategy Heading */}
          {!isMobile && (
            <div className="w-full pt-24 text-center pointer-events-none relative z-20">
              <motion.div style={{ opacity: strategyOpacity, y: strategyY }} className="flex flex-col items-center">
                <h2 className="text-4xl lg:text-[5vw] font-black uppercase leading-[0.9] tracking-tighter text-white px-4">
                  Strategy that gets <br className="hidden md:block" />
                  <span className="text-[#FFBF00]">results every day</span>
                </h2>
                <div className="h-[1px] w-64 bg-gradient-to-r from-transparent via-[#FFBF00] to-transparent mt-12 opacity-40" />
              </motion.div>
            </div>
          )}

          {/* Desktop Branding */}
          {!isMobile && (
            <div className="absolute inset-0 flex items-center justify-end px-6">
              <motion.div 
                style={{ opacity: brandOpacity, y: brandY }}
                className="w-full max-w-3xl lg:max-w-4xl flex flex-col items-end text-right pr-8"
              >
                <h1 className="text-[8vw] lg:text-[10vw] font-black uppercase leading-[0.8] tracking-tighter text-white mb-10">
                  {headingFirstLine}
                  {headingSecondLine && <><br /><span className="text-[#FFBF00]">{headingSecondLine}</span></>}
                </h1>
                <p className="text-gray-400 text-xl lg:text-2xl font-light max-w-2xl leading-relaxed mb-12">
                  {heroIntro.description}
                </p>
                
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleHeroButtonClick}
                  className="px-10 py-5 bg-[#FFBF00] text-white font-black uppercase tracking-widest text-sm rounded-full shadow-[0_20px_60px_rgba(255,107,53,0.3)] hover:shadow-[0_20px_80px_rgba(255,107,53,0.5)] transition-all z-50 relative pointer-events-auto"
                >
                  {heroIntro.button_text}
                </motion.button>
              </motion.div>
            </div>
          )}

          {/* Mobile - Pehli heading */}
          {isMobile && (
            <motion.div 
              style={{ opacity: mobileBrandOpacity, y: mobileBrandY }}
              className="absolute top-24 left-0 right-0 z-20 text-center px-4"
            >
              <h1 className="text-2xl md:text-3xl font-black uppercase leading-[1.1] tracking-tighter text-white mb-2">
                {headingFirstLine}
                {headingSecondLine && <><br /><span className="text-[#FFBF00]">{headingSecondLine}</span></>}
              </h1>
              <p className="text-gray-400 text-sm font-light max-w-lg mx-auto leading-relaxed mb-3">
                {heroIntro.description}
              </p>
              
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleHeroButtonClick}
                className="px-5 py-2 bg-[#FFBF00] text-white font-black uppercase tracking-widest text-xs rounded-full shadow-[0_20px_60px_rgba(255,107,53,0.3)] hover:shadow-[0_20px_80px_rgba(255,107,53,0.5)] transition-all z-50 relative pointer-events-auto"
              >
                {heroIntro.button_text}
              </motion.button>
            </motion.div>
          )}

          {/* Mobile - Strategy Heading */}
          {isMobile && (
            <motion.div 
              style={{ 
                opacity: mobileStrategyOpacity,
                position: 'absolute',
                left: 0,
                right: 0,
                top: '180px',
                zIndex: 25,
                textAlign: 'center',
                paddingLeft: '1rem',
                paddingRight: '1rem'
              }}
            >
              <h2 className="text-lg md:text-xl font-black uppercase leading-[1.2] tracking-tighter text-white">
                Strategy that gets <br />
                <span className="text-[#FFBF00]">results every day</span>
              </h2>
              <div className="h-[1px] w-10 bg-gradient-to-r from-transparent via-[#FFBF00] to-transparent mt-2 mx-auto opacity-40" />
            </motion.div>
          )}

          {/* Mobile Gallery */}
          {isMobile && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30">
              <div className="relative w-full h-full max-w-[340px] mx-auto overflow-visible">
                {loading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-white text-sm">Loading Hero Cards...</div>
                  </div>
                ) : heroCards.length > 0 ? (
                  heroCards.map((card, idx) => (
                    <HeroCardItem 
                      key={card.id} 
                      card={card} 
                      index={idx} 
                      totalCards={heroCards.length}
                      scrollYProgress={scrollYProgress}
                      isMobile={isMobile}
                    />
                  ))
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-white text-sm">No Hero Cards Available</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Desktop Gallery */}
          {!isMobile && (
            <div className="absolute inset-0 flex items-center justify-end pointer-events-none z-10">
              <div className="relative w-full h-full">
                {loading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-white text-xl">Loading Hero Cards...</div>
                  </div>
                ) : heroCards.length > 0 ? (
                  heroCards.map((card, idx) => (
                    <HeroCardItem 
                      key={card.id} 
                      card={card} 
                      index={idx} 
                      totalCards={heroCards.length}
                      scrollYProgress={scrollYProgress}
                      isMobile={isMobile}
                    />
                  ))
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-white text-xl">No Hero Cards Available</div>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>

        {/* Interaction Cue */}
        <motion.div 
          animate={{ y: [0, 10, 0], opacity: [0.3, 0.8, 0.3] }}
          transition={{ duration: 3, repeat: Infinity }}
          style={{ opacity: interactionCueOpacity }}
          className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 z-40"
        >
          <span className="text-[8px] font-black uppercase tracking-[1em] text-white/50">Scroll</span>
          <div className="w-[1px] h-8 md:h-32 bg-gradient-to-b from-[#FFBF00] to-transparent" />
        </motion.div>
      </motion.div>
    </section>
  );
};
